import React, { useState } from "react";
import EditableTextField from "../Input/EditableTextField";
import EditableSelectField from "../Input/EditableSelectField";
import { HiChevronDown, HiChevronUp, HiExclamation } from "react-icons/hi";

import {
    ProductInterestFormProps,
    ProductInterest,
} from "../../types/product_interest_form.t";

const WarningIcon = () => (
    <div className="" style={{ color: "#FF6A13" }}>
        <HiExclamation className="w-4 h-4 " />
    </div>
);

const ProductInterestForm: React.FC<ProductInterestFormProps> = ({
    index,
    interest,
    handleProductInterestChange,
    isEditing,
    setIsEditing,
}) => {
    const [isVisible, setIsVisible] = useState(false);

    // Function to toggle visibility
    const handleVisibility = () => {
        setIsVisible((prevState) => !prevState);
    };

    const isGroupValid = (interest: ProductInterest) => {
        // Check if volume or volume_unit is empty
        if (!interest.volume || !interest.volume_unit) {
            return true;
        }

        // Check if any attribute value is empty
        return Object.values(interest.attributes).some(
            (attr) => !attr.value || attr.value.trim() === ""
        );
    };

    // Handle dynamic attribute change
    const handleAttributeChange = (attrId: number, value: string) => {
        handleProductInterestChange(index, "attributes", {
            ...interest.attributes,
            [attrId]: {
                ...interest.attributes[attrId],
                value, // Update only the 'value' field
            },
        });
    };

    // Convert attributes from object to array to allow mapping
    const attributeEntries = Object.entries(interest.attributes).map(
        ([attribute_id, { name, type }]) => ({
            attribute_id: Number(attribute_id),
            name,
            type,
        })
    );

    return (
        <div className="mb-6 mt-6 border-b border-gray-200">
            {/* Product Name and Interest Checkbox */}
            <div>
                <div className="flex items-center flex-row">
                    <div className="w-1/2 font-semibold mr-4">
                        {interest.name}
                    </div>
                    <span className="flex font-semibold text-base w-1/6">
                        {isGroupValid(interest) && !isVisible && (
                            <WarningIcon />
                        )}
                    </span>
                    <div className="w-1/2 flex flex-row space-x-4 justify-end">
                        <button onClick={handleVisibility} className="text-lg">
                            {isVisible ? <HiChevronUp /> : <HiChevronDown />}
                        </button>
                    </div>
                </div>
            </div>

            {isVisible && (
                <div className="ml-4">
                    {/* Volume, Becs, Volume Unit Inputs */}
                    <div className="flex items-center space-x-4">
                        <EditableTextField
                            label="Volume"
                            name="volume"
                            value={interest.volume?.toString() || ""}
                            isEditing={isEditing}
                            error={false}
                            helperText=""
                            onChange={(e) =>
                                handleProductInterestChange(
                                    index,
                                    "volume",
                                    Number(e.target.value)
                                )
                            }
                        />
                        <EditableSelectField
                            label="Unité"
                            name="volume_unit"
                            value={interest.volume_unit || ""}
                            isEditing={isEditing}
                            options={[
                                { value: "HL", label: "HL" },
                                { value: "KG", label: "KG" },
                                { value: "Col", label: "Col" },
                            ]}
                            onChange={(e) =>
                                handleProductInterestChange(
                                    index,
                                    "volume_unit",
                                    e.target.value
                                )
                            }
                            fontSize="16px"
                        />
                    </div>

                    {/* Dynamic Attributes */}
                    {attributeEntries.map((attr) => (
                        <div key={attr.attribute_id} className="">
                            <EditableTextField
                                label={attr.name} // Display the attribute name
                                name={`attribute-${attr.attribute_id}`}
                                value={
                                    interest.attributes[attr.attribute_id]
                                        ?.value || ""
                                }
                                isEditing={isEditing}
                                error={false}
                                helperText=""
                                onChange={(e) =>
                                    handleAttributeChange(
                                        attr.attribute_id,
                                        e.target.value
                                    )
                                }
                            />
                        </div>
                    ))}
                </div>
            )}
            <div className="flex items-center mb-2 mt-4 flex-row">
                <div className="w-2/3 font-semibold text-sm mr-4">
                    Le client est interessé ?
                </div>
                <div className="w-1/3 flex flex-row space-x-4">
                    <button
                        onClick={() => {
                            setIsEditing();
                            handleProductInterestChange(
                                index,
                                "is_interested",
                                true
                            );
                        }}
                        className={`p-1 w-10 text-black rounded-full text-xxs ${
                            interest.is_interested ? "bg-gold" : "bg-gray-300"
                        }`}
                    >
                        Oui
                    </button>
                    <button
                        onClick={() => {
                            setIsEditing();
                            handleProductInterestChange(
                                index,
                                "is_interested",
                                false
                            );
                        }}
                        className={`p-1 w-10 rounded-full text-xxs ${
                            !interest.is_interested
                                ? "bg-rose-500 text-white"
                                : "bg-gray-300 text-black"
                        }`}
                    >
                        Non
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductInterestForm;
